import {
  ArrowDownOutlined,
  ArrowLeftOutlined,
  ArrowRightOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  MoreOutlined
} from "@ant-design/icons"
import { Button, Divider, Popover, Segmented } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"

import { homeColor, homeText } from "../../home-theme.ts"
import type { HomeCardEdit } from "../HomeCard.tsx"
import { WIDTH_PRESETS, type WidthPreset } from "./homeLayout"
import type { HomePageSectionId } from "./homePageLayout"
import { WidgetDragHandle } from "./WidgetEditChrome"

export interface UnitLayoutCommands {
  /** The widget's current name, so every control announces what it acts on. */
  title: string
  width: number
  minWidth: number
  section: HomePageSectionId
  sections: { id: HomePageSectionId; label: string }[]
  setWidth: (preset: WidthPreset) => void
  taller: () => void
  shorter: () => void
  move: (direction: "earlier" | "later") => void
  nudge: (direction: "left" | "right") => void
  moveToSection: (section: HomePageSectionId) => void
  rename: () => void
  remove: () => void
}

const PRESETS: { preset: WidthPreset; label: string }[] = [
  { preset: "third", label: "1/3" },
  { preset: "half", label: "1/2" },
  { preset: "twoThirds", label: "2/3" },
  { preset: "full", label: "Full" }
]

const rowStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  justifyContent: "space-between"
}

/**
 * Everything a drag or a resize can do, as plain buttons in one panel — so
 * the whole layout is editable from the keyboard. Every button names its
 * effect; the panel closes on Escape and on any action that moves the widget.
 */
const UnitLayoutPanel = ({
  commands,
  close
}: {
  commands: UnitLayoutCommands
  close: () => void
}) => {
  const { t } = useTranslation()
  const [choosingSection, setChoosingSection] = useState(false)
  const run = (action: () => void) => () => {
    action()
    close()
  }

  if (choosingSection) {
    return (
      <div
        style={{ display: "flex", flexDirection: "column", minWidth: 200 }}
        data-testid="home-unit-sections"
      >
        <Button
          type="text"
          size="small"
          icon={<ArrowLeftOutlined />}
          style={{ justifyContent: "flex-start" }}
          onClick={() => setChoosingSection(false)}
        >
          {t("home.layout.moveToSection", "Move to section")}
        </Button>
        {commands.sections.map((section) => (
          <Button
            key={section.id}
            type="text"
            size="small"
            disabled={section.id === commands.section}
            style={{ justifyContent: "flex-start" }}
            onClick={run(() => commands.moveToSection(section.id))}
          >
            {section.label}
          </Button>
        ))}
      </div>
    )
  }

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        minWidth: 220
      }}
      data-testid="home-unit-panel"
    >
      <Button
        type="text"
        size="small"
        style={{ justifyContent: "space-between", display: "flex" }}
        onClick={() => setChoosingSection(true)}
      >
        {t("home.layout.moveToSection", "Move to section")}
        <ArrowRightOutlined />
      </Button>

      <div>
        <div
          style={{
            ...homeText.meta,
            color: homeColor.inkMuted,
            marginBottom: 4
          }}
        >
          {t("home.layout.widthGroup", "Width")}
        </div>
        <Segmented
          size="small"
          block
          value={
            PRESETS.find((p) => WIDTH_PRESETS[p.preset] === commands.width)
              ?.preset
          }
          options={PRESETS.map((p) => ({
            value: p.preset,
            label:
              p.preset === "full" ? t("home.layout.full", "Full") : p.label,
            disabled: WIDTH_PRESETS[p.preset] < commands.minWidth
          }))}
          onChange={(value) => commands.setWidth(value as WidthPreset)}
          aria-label={t("home.layout.widthNamed", "Width of {{title}}", {
            title: commands.title
          })}
        />
      </div>

      <div style={rowStyle}>
        <span style={{ ...homeText.meta, color: homeColor.inkMuted }}>
          {t("home.layout.heightGroup", "Height")}
        </span>
        <span style={{ display: "inline-flex", gap: 4 }}>
          <Button size="small" onClick={commands.shorter}>
            {t("home.layout.shorter", "Shorter")}
          </Button>
          <Button size="small" onClick={commands.taller}>
            {t("home.layout.taller", "Taller")}
          </Button>
        </span>
      </div>

      <div style={rowStyle}>
        <span style={{ ...homeText.meta, color: homeColor.inkMuted }}>
          {t("home.layout.positionGroup", "Position")}
        </span>
        <span style={{ display: "inline-flex", gap: 4 }}>
          <Button
            size="small"
            icon={<ArrowUpOutlined />}
            aria-label={t("home.layout.moveEarlier", "Move up")}
            onClick={() => commands.move("earlier")}
          />
          <Button
            size="small"
            icon={<ArrowDownOutlined />}
            aria-label={t("home.layout.moveLater", "Move down")}
            onClick={() => commands.move("later")}
          />
          <Button
            size="small"
            icon={<ArrowLeftOutlined />}
            aria-label={t("home.layout.moveLeft", "Move left")}
            disabled={commands.width >= 12}
            onClick={() => commands.nudge("left")}
          />
          <Button
            size="small"
            icon={<ArrowRightOutlined />}
            aria-label={t("home.layout.moveRight", "Move right")}
            disabled={commands.width >= 12}
            onClick={() => commands.nudge("right")}
          />
        </span>
      </div>

      <Divider style={{ margin: "2px 0" }} />
      <Button
        type="text"
        size="small"
        icon={<EditOutlined />}
        style={{ justifyContent: "flex-start" }}
        onClick={run(commands.rename)}
      >
        {t("home.layout.rename", "Rename")}
      </Button>
      <Button
        type="text"
        size="small"
        danger
        icon={<DeleteOutlined />}
        style={{ justifyContent: "flex-start" }}
        data-testid="widget-remove"
        onClick={run(commands.remove)}
      >
        {t("home.layout.removeWidget", "Remove widget")}
      </Button>
    </div>
  )
}

const UnitLayoutMenu = ({ commands }: { commands: UnitLayoutCommands }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      trigger={["click"]}
      placement="bottomRight"
      destroyOnHidden
      content={
        <UnitLayoutPanel commands={commands} close={() => setOpen(false)} />
      }
    >
      <Button
        type="text"
        size="small"
        data-testid="home-unit-layout-menu"
        aria-label={t("home.layout.menuNamed", "Layout options for {{title}}", {
          title: commands.title
        })}
        aria-haspopup="dialog"
        aria-expanded={open}
        icon={<MoreOutlined />}
        style={{
          width: 24,
          height: 24,
          minWidth: 24,
          color: homeColor.inkMuted
        }}
      />
    </Popover>
  )
}

/**
 * The handle and the layout menu, for the widget's OWN header. Edit layout
 * adds no row of its own: the handle leads the title, the menu closes the
 * header's actions.
 */
export const unitEditChrome = (commands: UnitLayoutCommands): HomeCardEdit => ({
  handle: (
    <WidgetDragHandle
      title={commands.title}
      onMove={(direction) => {
        if (direction === "up") commands.move("earlier")
        else if (direction === "down") commands.move("later")
        else commands.nudge(direction)
      }}
    />
  ),
  controls: <UnitLayoutMenu commands={commands} />
})
